/* eslint-disable react/jsx-no-target-blank */
import React, { useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";
import { useSelector } from "react-redux";
import { stringify } from "qs";
import { space } from "main/javascripts/styles/base/spaceStyle";
import {
  accentColor,
  shadeColor,
} from "main/javascripts/styles/base/colorStyle";
import {
  fontSize,
  fontWeight,
  letterSpacing,
} from "main/javascripts/styles/base/typographyStyle";
import {
  LINE_ENTRY_LIFF_URL,
  PATH_SCOPE,
} from "main/javascripts/constants/Constants";
import {
  isPhone,
  moreThanBreakpoint,
} from "main/javascripts/styles/base/responsiveStyle";
import { useTranslation } from "react-i18next";
import { useScroll } from "main/javascripts/hooks/common/useScroll";
import { getLineEntryValues } from "main/javascripts/utils/trackingUtil";
import { GoogleTagManagerEventTypes } from "main/javascripts/constants/GoogleTagManagerEventTypes";
import { SvgIcon } from "../../icon/SvgIcon";
import { isMobile } from "main/javascripts/utils/DeviceUtil";
import { useAppSelector } from "main/javascripts/store";
import { isTrackingParameterStoredSelector } from "main/javascripts/slices/paramSlice";

const iconLinePath = `${PATH_SCOPE}/main/images/icons/line.svg`;

export const FixedConsultationButtonBlock: React.FC =
  (): React.ReactElement => {
    const { t } = useTranslation(["component", "button"]);

    const isTrackingParameterStored = useAppSelector(
      isTrackingParameterStoredSelector
    );
    // TODO: 旧reducer
    const breakpoint = useSelector<any>(
      (state) => state.breakpointReducer.breakpoint
    );
    const isPhoneWidthRef = useRef(isPhone(breakpoint));
    isPhoneWidthRef.current = isPhone(breakpoint);

    const emailConsultationBasePath = "/mail_cruise_consultations/new";

    const [showLineButton, setShowLineButton] = useState(false);
    const [lineEntryUrl, setLineEntryUrl] = useState(LINE_ENTRY_LIFF_URL);
    const [emailConsultationPath, setEmailConsultationPath] = useState(
      emailConsultationBasePath
    );
    const [hideButton, setHideButton] = useState(isPhoneWidthRef.current);

    useEffect(() => {
      setShowLineButton(isMobile());
    }, []);

    useEffect(() => {
      if (isTrackingParameterStored) {
        const params = getLineEntryValues();
        const newParams = { ...params, cruise: true };
        const queryString = stringify(newParams, { addQueryPrefix: true });
        setLineEntryUrl(`${LINE_ENTRY_LIFF_URL}${queryString}`);
        setEmailConsultationPath(`${emailConsultationBasePath}${queryString}`);
      }
    }, [isTrackingParameterStored]);

    useScroll((currentScrollY: number) => {
      if (!isPhoneWidthRef.current) {
        setHideButton(false);
        return;
      }
      if (currentScrollY > 100) {
        setHideButton(false);
      } else {
        setHideButton(true);
      }
    });

    const blockStyles = hideButton ? [blockStyle, hiddenStyle] : blockStyle;
    const emailButtonTextStyles = showLineButton ? textStyle : textLargeStyle;

    return (
      <div css={blockStyles}>
        <a
          css={buttonStyle}
          href={emailConsultationPath}
          data-event-category={
            GoogleTagManagerEventTypes.clickCruiseConsultation
          }
        >
          <div css={innerEmailStyle}>
            <div css={symbolStyle}>
              <SvgIcon name="email" size="32px" />
            </div>
            <div css={emailButtonTextStyles}>
              <span css={lineFooterTextStyle}>
                {t("button:consultation.consultViaPlatformShort", {
                  platform: t("constant:platformType.email"),
                })}
              </span>
            </div>
          </div>
          <div css={badgeStyle}>{t("post.free")}</div>
        </a>
        {showLineButton && (
          <a
            css={buttonStyle}
            href={lineEntryUrl}
            data-event-category={
              GoogleTagManagerEventTypes.clickCruiseConsultation
            }
          >
            <div css={innerLineStyle}>
              <div css={symbolStyle}>
                <img src={iconLinePath} alt="LINE" />
              </div>
              <div css={textStyle}>
                <span css={lineFooterTextStyle}>
                  {t("button:consultation.consultViaPlatformShort", {
                    platform: t("constant:platformType.line"),
                  })}
                </span>
              </div>
            </div>
            <div css={badgeStyle}>{t("post.free")}</div>
          </a>
        )}
      </div>
    );
  };

const blockStyle = css`
  position: fixed;
  bottom: 0;
  right: 0;
  width: 100%;
  display: flex;
  gap: ${space.atom};
  margin: 0 auto;
  padding: ${space.atom} ${space.atom} ${space.atom1_5x};
  background-color: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(40px) saturate(1.2);
  box-sizing: border-box;
  z-index: 1;
  transition: opacity 0.6s ease, visibility 0.6s ease;
  ${moreThanBreakpoint("tablet")} {
    bottom: ${space.atom2x};
    right: ${space.atom2x};
    width: fit-content;
    background: 0;
    backdrop-filter: none;
    gap: ${space.atom3x};
  }
  ${moreThanBreakpoint("desktop")} {
    bottom: ${space.atom3x};
    right: ${space.atom3x};
  }
`;
const hiddenStyle = css`
  opacity: 0;
  visibility: hidden;
`;
const buttonStyle = css`
  display: flex;
  width: 100%;
  max-width: 18rem;
  min-height: 2.75rem;
  text-decoration: none;
  margin: 0 auto;
  position: relative;
  ${moreThanBreakpoint("tablet")} {
    width: auto;
    min-width: 12rem;
    min-height: 3.125rem;
    filter: drop-shadow(0 0 8px rgb(0 0 0 / 0.1));
  }
`;
const innerStyle = css`
  display: flex;
  width: 100%;
  background: ${accentColor.lineColor};
  box-shadow: 0 4px 0 ${shadeColor(accentColor.lineColor, -20)};
  border-radius: 0.5rem;
  color: #fff;
  transition: background-color 0.3s ease, box-shadow 0.3s ease;
  &:hover {
    background-color: ${shadeColor(accentColor.lineColor, 5)};
    box-shadow: 0 4px 0 ${shadeColor(accentColor.lineColor, -10)};
  }
`;
const innerLineStyle = css`
  ${innerStyle};
  background: ${accentColor.lineColor};
  box-shadow: 0 4px 0 ${shadeColor(accentColor.lineColor, -20)};
  &:hover {
    background-color: ${shadeColor(accentColor.lineColor, 5)};
    box-shadow: 0 4px 0 ${shadeColor(accentColor.lineColor, -10)};
  }
`;
const innerEmailStyle = css`
  ${innerStyle};
  background: ${accentColor.primaryColor};
  box-shadow: 0 4px 0 ${shadeColor(accentColor.primaryColor, -20)};
  &:hover {
    background-color: ${shadeColor(accentColor.primaryColor, 8)};
    box-shadow: 0 4px 0 ${shadeColor(accentColor.primaryColor, -8)};
  }
`;
const lineFooterTextStyle = css`
  margin: auto;
`;
const symbolStyle = css`
  position: relative;
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  box-sizing: border-box;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  img {
    width: 26px;
  }
  ${moreThanBreakpoint("tablet")} {
    display: flex;
  }
`;
const textStyle = css`
  font-size: 0.875rem;
  font-weight: ${fontWeight.medium};
  line-height: 1.4;
  padding: ${space.atom1_5x} ${space.atom} ${space.atom1_5x} ${space.atom};
  display: flex;
  align-items: center;
  flex: 1;
  letter-spacing: ${letterSpacing.text};
  ${moreThanBreakpoint("tablet")} {
    font-size: ${fontSize.body};
    padding: ${space.atom} ${space.atom3x} ${space.atom} 0;
  }
`;
const textLargeStyle = css`
  ${textStyle};
  font-size: ${fontSize.body};
`;
const badgeStyle = css`
  position: absolute;
  width: 42px;
  height: 24px;
  text-align: center;
  line-height: 24px;
  font-size: 12px;
  font-weight: ${fontWeight.bold};
  top: -16px;
  right: -6px;
  background-color: ${accentColor.secondaryLightColor};
  border: 2px solid #fff;
  color: #fff;
  border-radius: 16px;
  ${moreThanBreakpoint("tablet")} {
    right: -10px;
  }
`;
